import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'

import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'

import { enumEnumToValueString, enumToSelectOptionArray } from '../../../helpers'

import { getCustomerDomain } from '../../../redux/helpers'

import {
  EnumCommodity,
  EnumDivisa,
  EnumTipoAdhesionCFlotante,
  EnumTipoConceptoFlotante,
  EnumUnidadMedida
} from '../../../api'
import { NonAuthRoutes } from '../../../model'

import { Concept } from '../../../api/Argentia/endpoints/concepts/model/concepts'
import { FloatingConceptsModel } from '../../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import { ContractModel } from '../../../api/Argentia/model/contracts'
import { fetchSubjects } from '../../../services/subjects.service'
import { fetchConcepts } from '../../concepts/concepts.service'
import { fetchContractsBySubjectID } from '../../contracts/contracts.service'
import { mapContractsToSelectOptions } from '../../procedures/procedures.mapper'
import { fetchSettlementGroupCommodity } from '../../settlementGroups/settlementGroups.service'
import {
  baseConceptsMapperToDropDown,
  mapFloatingConceptFormDataToPostRequest
} from '../floatingConcepts.mapper'
import { EnumFloatingConceptsTabs, FloatingConceptsFormAddModel } from '../floatingConcepts.model'
import { createFloatingConcept } from '../floatingConcepts.service'

interface FloatingConceptsAddPageProps {
  location: {
    state?: {
      activeTab: EnumFloatingConceptsTabs
      settlementGroupOptionsList?: SelectOption[]
      floatingConceptType?: EnumTipoConceptoFlotante
      adhesionType?: EnumTipoAdhesionCFlotante
    }
  }
}

export const FloatingConceptsAddPage: React.FC<FloatingConceptsAddPageProps> = props => {
  const history = useHistory()
  const [t] = useTranslation(['floatingConceptsAdd', 'globalerisLibrary', 'enums'])
  const { info, error } = useNotify()

  const [formData, setFormData] = useState<FloatingConceptsFormAddModel>(
    {} as FloatingConceptsFormAddModel
  )
  const [subjectContracts, setSubjectContracts] = useState<ContractModel[]>([])
  const [baseConcepts, setBaseConcepts] = useState<Concept[]>([])
  const [isLoadingBaseConcepts, setIsLoadingBaseConcepts] = useState<boolean>(false)

  const [baseConceptsOptions, setBaseConceptsOptions] = useState<SelectOption[]>([])
  const [masterConceptCurrency, setMasterConceptCurrency] = useState<EnumDivisa>()
  const [unitPrice, setUnitPrice] = useState<string>()

  const [isFetchingContracts, setIsFetchingContracts] = useState<boolean>(false)

  const activeTab = props.location.state?.activeTab
  const isBalanceActionsTab = activeTab === EnumFloatingConceptsTabs.balanceActions
  const isFloatingConceptsTab =
    activeTab !== EnumFloatingConceptsTabs.virtualBattery && !isBalanceActionsTab
  const isRightsTab = false

  const settlementGroupOptionsList = props.location.state?.settlementGroupOptionsList ?? []
  const defaultFloatingConceptType = props.location.state?.floatingConceptType
  const defaultAdhesionType = props.location.state?.adhesionType

  if (activeTab === undefined || activeTab === null) {
    history.goBack()
    return <></>
  }

  // Handlers Starts
  const onFormSaveHandler = async (formData: FloatingConceptsModel): Promise<boolean> => {
    if (formData) {
      if (!formData.price || !formData.quantity) {
        error(
          !formData.price
            ? t('floatingConceptsAdd:notifications.create.priceZeroNotAllowed')
            : t('floatingConceptsAdd:notifications.create.quantityZeroNotAllowed')
        )
        return false
      }

      const request = mapFloatingConceptFormDataToPostRequest(formData)
      return await createFloatingConcept(request).then(response => {
        if (response.success) {
          info(t('floatingConceptsAdd:notifications.create.success'))
          history.push(NonAuthRoutes.floatingConcepts)
          return true
        } else {
          error(t('floatingConceptsAdd:notifications.create.error'))
          return false
        }
      })
    } else {
      history.push(NonAuthRoutes.floatingConcepts)
      return false
    }
  }

  // Handlers Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts

  const getSubjectContracts = subjectId => {
    setIsFetchingContracts(true)
    fetchContractsBySubjectID(subjectId).then(response => {
      if (response.success) {
        response.data && setSubjectContracts(response.data)
      } else {
        error(t('floatingConcepts:notifications.getSubjectContracts.error'))
      }
      setIsFetchingContracts(false)
    })
  }

  const getBaseConcepts = async (commodity: EnumCommodity) => {
    let request = {} as {
      clientType: EnumClientTypes
      commodity: EnumCommodity
      includeInactives: boolean | null
      lgBypass: boolean | null
    }

    switch (activeTab) {
      case EnumFloatingConceptsTabs.distribucion:
      case EnumFloatingConceptsTabs.refacturacion:
      case EnumFloatingConceptsTabs.penalizacion:
      case EnumFloatingConceptsTabs.manual:
        request = {
          clientType: EnumClientTypes.FINAL_CLIENT,
          commodity,
          includeInactives: false,
          lgBypass: null
        }
        break
      case EnumFloatingConceptsTabs.balanceActions:
        request = {
          clientType: EnumClientTypes.SHIPPER,
          commodity,
          includeInactives: null,
          lgBypass: true
        }
        break
      default:
        request = {
          clientType: EnumClientTypes.FINAL_CLIENT,
          commodity,
          includeInactives: null,
          lgBypass: null
        }
        break
    }

    await fetchConcepts(request)
      .then(response => {
        setIsLoadingBaseConcepts(true)
        if (response.success) {
          response.data && setBaseConcepts(response.data)
          response.data && setBaseConceptsOptions(baseConceptsMapperToDropDown(response.data))
        }
      })
      .finally(() => {
        setIsLoadingBaseConcepts(false)
      })
  }

  const getUnitPrice = (
    currency: EnumDivisa | undefined,
    unitMeasure: EnumUnidadMedida | undefined
  ): string => {
    const currencySign = t(
      `enums:CurrencySigns.${enumEnumToValueString(EnumDivisa, currency ?? EnumDivisa.EUR)}`
    )

    return (
      currencySign +
      '/' +
      enumEnumToValueString(EnumUnidadMedida, unitMeasure ?? EnumUnidadMedida.Kwh)
    )
  }

  const configCurrencySign = (): string => {
    return masterConceptCurrency
      ? t(`enums:CurrencySigns.${enumEnumToValueString(EnumDivisa, masterConceptCurrency)}`)
      : ''
  }

  // Functions Ends

  // Form Definition Starts

  const addFormSections: Section[] = [
    {
      title: t('floatingConceptsAdd:forms.add.titleSection1'),
      inputs: [
        {
          name: 'subject',
          display: isFloatingConceptsTab || isRightsTab,
          label: t('floatingConceptsAdd:forms.add.inputs.subject'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchSubjects,
          placeholder: t('floatingConceptsAdd:forms.add.placeholder.subject'),
          onChange: (subjectSelected: any, formState: any) => {
            if (subjectSelected) {
              //clean previous contracts and concepts from FormData
              setFormData({
                ...formState,
                contracts: []
              })
              setBaseConcepts([])
              setBaseConceptsOptions([])
              //update form MULTISELECT values if field is not Null
              getSubjectContracts(subjectSelected.value)
            } else {
              setSubjectContracts([])
              setBaseConcepts([])
              setBaseConceptsOptions([])
            }
          }
        },
        {
          name: 'contractId',
          display: isFloatingConceptsTab || isRightsTab,
          label: t('floatingConceptsAdd:forms.add.inputs.contract'),
          type: DataType.SELECT,
          selectOptions: mapContractsToSelectOptions(subjectContracts),
          isLoading: isFetchingContracts,
          helpText: t('floatingConceptsAdd:forms.add.helptext.contractId'),
          onChange: (selectedContractId: string | number, formState: any) => {
            setBaseConcepts([])
            setBaseConceptsOptions([])
            if (selectedContractId) {
              const selectedContract = subjectContracts.find(c => c.id === +selectedContractId)
              if (selectedContract?.commodity !== undefined) {
                getBaseConcepts(selectedContract.commodity)
              }
            }
          }
        },
        {
          name: 'settlementGroupId',
          display: isBalanceActionsTab,
          label: t('floatingConceptsAdd:forms.add.inputs.settlementGroup'),
          type: DataType.SELECT,
          selectOptions: settlementGroupOptionsList ?? [],
          onChange: (selectedSettlementGroupId: string | number, formState: any) => {
            setBaseConcepts([])
            setBaseConceptsOptions([])
            if (selectedSettlementGroupId) {
              fetchSettlementGroupCommodity(+selectedSettlementGroupId).then(response => {
                if (response.success && response.data !== undefined && response.data !== null) {
                  getBaseConcepts(response.data)
                } else {
                  error(t('floatingConcepts:notifications.getSettlementGroupCommodity.error'))
                }
              })
            }
          }
        },
        {
          name: 'masterConceptId',
          label: t('floatingConceptsAdd:forms.add.inputs.baseConcept'),
          required: true,
          type: DataType.SELECT,
          selectOptions: baseConceptsOptions,
          onChange: (selectedConceptId: string | number, formState: any) => {
            const selectedConcept = baseConcepts.find(concept => concept.id === +selectedConceptId)
            setFormData({
              ...formState,
              conceptName: selectedConcept?.description,
              conceptCode: selectedConcept?.code,
              calculationType: selectedConcept?.calculationType,
              unitOfMeasure: selectedConcept?.unitMeasure
            })
            setMasterConceptCurrency(selectedConcept?.currency ?? EnumDivisa.EUR)
            setUnitPrice(getUnitPrice(selectedConcept?.currency, selectedConcept?.unitMeasure))
          }
        },
        {
          name: 'conceptName',
          label: t('floatingConceptsAdd:forms.add.inputs.description'),
          type: DataType.TEXT,
          inputSize: isBalanceActionsTab ? 6 : undefined
        },
        {
          name: 'floatingConceptType',
          label: t('floatingConceptsAdd:forms.add.inputs.floatingConceptType'),
          type: DataType.SELECT,
          defaultValue: defaultFloatingConceptType,
          readOnly: true,
          selectOptions: enumToSelectOptionArray(
            EnumTipoConceptoFlotante,
            'enums:EnumTipoConceptoFlotante'
          )
        },
        {
          name: 'adhesionType',
          readOnly: isBalanceActionsTab || isRightsTab,
          label: t('floatingConceptsAdd:forms.add.inputs.adhesion'),
          type: DataType.SELECT,
          defaultValue: defaultAdhesionType,
          selectOptions: enumToSelectOptionArray(
            EnumTipoAdhesionCFlotante,
            'enums:EnumTipoAdhesionCFlotante'
          )
        },
        {
          name: 'conceptDetail',
          label: t('floatingConceptsAdd:forms.add.inputs.conceptDetail'),
          type: DataType.TEXT,
          inputSize: 6
        }
      ]
    },
    {
      title: t('floatingConceptsAdd:forms.add.titleSection2'),
      inputs: [
        {
          name: 'startDate',
          label: t('floatingConceptsAdd:forms.add.inputs.initDate'),
          type: DataType.DATE,
          required: true
        },
        {
          name: 'endDate',
          label: t('floatingConceptsAdd:forms.add.inputs.endDate'),
          type: DataType.DATE,
          required: true
        }
      ]
    },
    {
      title: t('floatingConceptsAdd:forms.add.titleSection3'),
      inputs: [
        {
          name: 'price',
          label: t('floatingConceptsAdd:forms.add.inputs.unitPrice'),
          type: DataType.NUMBER,
          customDecimalScale: 8,
          suffix: unitPrice,
          inputSize: 4
        },
        {
          name: 'quantity',
          label: t('floatingConceptsAdd:forms.add.inputs.quantity'),
          type: DataType.NUMBER,
          customDecimalScale: 8,
          suffix: enumEnumToValueString(EnumUnidadMedida, formData.unitOfMeasure),
          inputSize: 4
        },
        {
          name: 'amount',
          label: t('floatingConceptsAdd:forms.add.inputs.amount'),
          type: DataType.NUMBER,
          customDecimalScale: 8,
          suffix: configCurrencySign(),
          required: true,
          inputSize: 4
        }
      ]
    }
  ]

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('floatingConceptsAdd:forms.add.analytics')
    },
    onSave: onFormSaveHandler
  }

  // Form Config Ends

  // Hooks Starts

  // Hooks Ends

  return (
    <Page>
      <PageHeader title={t('floatingConceptsAdd:title')}></PageHeader>
      <>
        <Form sections={addFormSections} data={formData} options={options} />
      </>
    </Page>
  )
}
